import { Button } from "@jutro/ui";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { ConfirmDialog } from "components/new/ConfirmDialog";
import {
  EdmGetAllShiftsDocument,
  EdmMedicalAppShiftWithoutVisitFragment,
  useEdmCancelShiftsConfirmationMutation,
  useEdmConfirmShiftsMutation,
} from "lib/graphql/megaSchema";
import { toaster } from "lib/tools/toaster";

type Props = {
  selectedShiftRows: EdmMedicalAppShiftWithoutVisitFragment[];
  setSelectedShiftRows: Dispatch<
    SetStateAction<EdmMedicalAppShiftWithoutVisitFragment[]>
  >;
};
export const SelectedShiftRowsHeader = ({
  selectedShiftRows,
  setSelectedShiftRows,
}: Props) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalMode, setConfirmModalMode] = useState<
    "confirm" | "cancel"
  >("confirm");

  const [confirmShifts] = useEdmConfirmShiftsMutation();
  const [cancelShiftsConfirmation] = useEdmCancelShiftsConfirmationMutation();

  const isDialogInConfirmMode = confirmModalMode === "confirm";

  const nonConfirmedShiftIds = useMemo(
    () =>
      selectedShiftRows
        .filter(({ confirmed }) => !confirmed)
        .map(({ id }) => id),
    [selectedShiftRows],
  );

  const confirmedShiftIds = useMemo(
    () =>
      selectedShiftRows
        .filter(({ confirmed }) => confirmed)
        .map(({ id }) => id),
    [selectedShiftRows],
  );

  const handleConfirmAll = () => {
    const confirmShiftPromise = confirmShifts({
      variables: {
        input: { shiftIds: nonConfirmedShiftIds },
      },
      refetchQueries: [EdmGetAllShiftsDocument],
    });

    toaster.promise(confirmShiftPromise, {
      loading: "Potwierdzam zaznaczone obecności...",
      success: "Poprawnie potwierdzono zaznaczone obecności",
      error: "Wystąpił błąd",
    });

    setSelectedShiftRows([]);
  };

  const handleCancelAll = () => {
    const cancelShiftConfirmationPromise = cancelShiftsConfirmation({
      variables: { input: { shiftIds: confirmedShiftIds } },
      refetchQueries: [EdmGetAllShiftsDocument],
    });

    toaster.promise(cancelShiftConfirmationPromise, {
      loading: "Odwołuje zaznaczone obecności...",
      success: "Poprawnie odwołano zaznaczone obecności",
      error: "Wystąpił błąd",
    });

    setSelectedShiftRows([]);
  };

  return (
    <>
      <div className="flex gap-2 whitespace-nowrap">
        <Button
          size="condensed"
          text="Potwierdź zaznaczone"
          onClick={() => {
            setIsConfirmModalOpen(true);
            setConfirmModalMode("confirm");
          }}
          disabled={nonConfirmedShiftIds.length === 0}
        />
        <Button
          size="condensed"
          text="Odwołaj zaznaczone"
          onClick={() => {
            setIsConfirmModalOpen(true);
            setConfirmModalMode("cancel");
          }}
          variant="negative"
          disabled={confirmedShiftIds.length === 0}
        />
      </div>

      <ConfirmDialog
        open={isConfirmModalOpen}
        setOpen={setIsConfirmModalOpen}
        title={`Czy na pewno chcesz ${
          isDialogInConfirmMode ? "potwierdzić" : "odwołać"
        } wszystkie zaznaczone obecności?`}
        text=""
        submitVariant={isDialogInConfirmMode ? "primary" : "negative"}
        confirmText={isDialogInConfirmMode ? "Potwierdź" : "Odwołaj"}
        onConfirm={() =>
          isDialogInConfirmMode ? handleConfirmAll() : handleCancelAll()
        }
      />
    </>
  );
};
